import React, { ReactElement, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { SpeedDial } from 'primereact/speeddial';
import { Tooltip } from 'primereact/tooltip';
import { MenuItem } from 'primereact/menuitem';
import { CORPORATE_DASHBOARD_DEFAULT } from '@Constants/constants';
import { DashboardSetting, DashboardFiltersResponse } from '@Apis/companyDashboard';
import { TASK_FILTER_ID_FIELDS, LOCATION_FILTER_ID_FIELDS } from '../filterFields';
import FilterDialog, { FilterGroupConfig } from './FilterDialog';
import './OverviewFilters.scss';

const { CORPORATE_DASHBOARD_VIEW_DEFAULT_ID } = CORPORATE_DASHBOARD_DEFAULT;

type OverviewFilters = DashboardFiltersResponse['filters'];
type ActiveDialog = 'task' | 'location' | null;

export interface OverviewFilterControlsHandle {
    openTaskFilters: () => void;
    openLocationFilters: () => void;
}

interface OverviewFilterControlsProps {
    filters: OverviewFilters;
    settings: DashboardSetting;
    defaultSettings: DashboardSetting;
    onApply: (settings: DashboardSetting) => void;
}

// id-array fields that belong to the Task Filters dialog (used for the active-count badge and Clear)
const TASK_ID_FIELDS: Array<keyof DashboardSetting> = [...TASK_FILTER_ID_FIELDS];

// id-array fields that belong to the Location Filters dialog (used for the active-count badge and Clear)
const LOCATION_ID_FIELDS: Array<keyof DashboardSetting> = [...LOCATION_FILTER_ID_FIELDS];

// Renders the Task Filters / Location Filters modals plus a bottom-right floating SpeedDial button.
// The toolbar's "Filters" button and the breakdown panel's "adjust filters" nudge open the same
// dialogs via the imperative handle below, so all three entry points share one filter UI.
const OverviewFilterControls = forwardRef<OverviewFilterControlsHandle, OverviewFilterControlsProps>(
    ({ filters, settings, defaultSettings, onApply }, ref): ReactElement => {
        const [draft, setDraft] = useState<DashboardSetting>(settings);
        const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);
        const tooltipRef = useRef<Tooltip>(null);

        // Re-sync draft when settings change externally (e.g. a saved view is selected, or filters are cleared elsewhere)
        useEffect(() => {
            setDraft(settings);
        }, [settings]);

        useImperativeHandle(ref, () => ({
            openTaskFilters: () => setActiveDialog('task'),
            openLocationFilters: () => setActiveDialog('location'),
        }), []);

        const taskGroups = useMemo<FilterGroupConfig[]>(
            () => [
                { field: 'disciplineIds', label: 'Disciplines', options: filters.disciplines },
                { field: 'departmentIds', label: 'Departments', options: filters.departments },
                { field: 'isMilestonesOnly', label: 'Milestones', type: 'milestone' },
                { field: 'phaseIds', label: 'Phases', options: filters.phases },
                { field: 'communityIds', label: 'Communities', options: filters.communities },
                { field: 'baselineIds', label: 'Baselines', options: filters.baselines },
                { field: 'projectIds', label: 'Projects', options: filters.projects },
            ],
            [filters]
        );

        const locationGroups = useMemo<FilterGroupConfig[]>(
            () => [
                { field: 'brandIds', label: 'Brands', options: filters.brands },
                { field: 'areaIds', label: 'Areas', options: filters.areas },
                { field: 'regionIds', label: 'Regions', options: filters.regions },
                { field: 'managementCompanyIds', label: 'Management Companies', options: filters.managementCompanies },
                { field: 'locationTypeIds', label: 'Types', options: filters.locationTypes },
                { field: 'continentIds', label: 'Continents', options: filters.continents },
                { field: 'locationIds', label: 'Specific Locations', options: filters.locationsByAbbreviation },
                { field: 'tagIds', label: 'Tags', options: filters.tags },
            ],
            [filters]
        );

        const activeCount = useMemo(() => {
            const idFieldsCount = [...TASK_ID_FIELDS, ...LOCATION_ID_FIELDS].reduce((count, field) => {
                const value = draft[field];
                return Array.isArray(value) && value.length > 0 ? count + 1 : count;
            }, 0);
            return idFieldsCount + (draft.isMilestonesOnly === true ? 1 : 0);
        }, [draft]);

        const buildAppliedSettings = (nextDraft: DashboardSetting): DashboardSetting => ({
            ...nextDraft,
            updates: nextDraft.updates + 1,
            id: CORPORATE_DASHBOARD_VIEW_DEFAULT_ID,
            name: undefined,
        });

        const onFieldChange = (field: keyof DashboardSetting, value: number[] | boolean) => {
            setDraft((prev) => ({ ...prev, [field]: value }));
        };

        const onDialogClear = (fields: Array<keyof DashboardSetting>) => {
            setDraft((prev) => {
                const next = { ...prev };
                fields.forEach((field) => {
                    (next as Record<keyof DashboardSetting, unknown>)[field] = defaultSettings[field];
                });
                return next;
            });
        };

        const onDialogApply = () => {
            onApply(buildAppliedSettings(draft));
            setActiveDialog(null);
        };

        const clearAll = () => {
            const cleared = { ...defaultSettings };
            setDraft(cleared);
            onApply(buildAppliedSettings(cleared));
        };

        // The Tooltip is bound to the speed-dial actions by selector, so it only learns to hide
        // from a mouseleave on the action it is showing for. "Clear All Filters" removes its own
        // action from the model, and an unmounted element never fires that mouseleave — the label
        // was left floating over the page. Every command hides the tooltip explicitly instead.
        const runCommand = (action: () => void) => () => {
            tooltipRef.current?.hide();
            action();
        };

        const speedDialModel: MenuItem[] = [
            { label: 'Task Filters', icon: 'fa-regular fa-list-check', command: runCommand(() => setActiveDialog('task')) },
            { label: 'Location Filters', icon: 'fa-regular fa-location-dot', command: runCommand(() => setActiveDialog('location')) },
        ];

        if (activeCount > 0) {
            // The bundled Font Awesome subset has no filter-slash/filter-circle-xmark glyph
            speedDialModel.push({ label: 'Clear All Filters', icon: 'fa-regular fa-circle-xmark', command: runCommand(clearAll) });
        }

        return (
            <>
                <div className='overview-filter-speeddial-wrapper'>
                    <Tooltip ref={tooltipRef} target='.overview-filter-speeddial-wrapper .p-speeddial-action' position='left' />
                    <SpeedDial
                        model={speedDialModel}
                        direction='up'
                        radius={80}
                        showIcon='pi pi-filter'
                        hideIcon='pi pi-times'
                        buttonClassName='overview-filter-fab'
                    />
                </div>

                <FilterDialog
                    title='Task Filters'
                    visible={activeDialog === 'task'}
                    onHide={() => setActiveDialog(null)}
                    groups={taskGroups}
                    draft={draft}
                    onChange={onFieldChange}
                    onClear={() => onDialogClear([...TASK_ID_FIELDS, 'isMilestonesOnly'])}
                    onApply={onDialogApply}
                />

                <FilterDialog
                    title='Location Filters'
                    visible={activeDialog === 'location'}
                    onHide={() => setActiveDialog(null)}
                    groups={locationGroups}
                    draft={draft}
                    onChange={onFieldChange}
                    onClear={() => onDialogClear(LOCATION_ID_FIELDS)}
                    onApply={onDialogApply}
                />
            </>
        );
    }
);

OverviewFilterControls.displayName = 'OverviewFilterControls';

export default OverviewFilterControls;
